import { DatePipe } from '@angular/common';
import {
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
  inject,
  signal,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideSearch } from '@ng-icons/lucide';
import { Subscription, finalize, take } from 'rxjs';
import { ColumnDef } from '@3ddv/software-division-components/backoffice/table';
import { DateRangeComponent } from '@3ddv/software-division-components/backoffice/daterange';
import { InputComponent } from '@3ddv/software-division-components/backoffice/input';
import { ButtonComponent } from '@3ddv/software-division-components/generic/button';
import { SelectComponent } from '@3ddv/software-division-components/generic/select';
import { LoaderService } from '../../core/services/loader.service';
import { GsTableComponent } from '../../shared/ui/table/gs-table.component';
import { AppDialogService } from '../layout/dialog/app-dialog.service';
import { PaginationComponent } from '../layout/pagination/pagination.component';
import { ConfigurationService } from '../services/configuration.service';
import { ViewComponent } from './modals/view/view.component';
import {
  SearchQueryParams,
  Transaction,
  TransactionStatusObj,
} from './models/transaction.model';
import { TransactionService } from './services/transactions.service';
import { buildTransactionColumns } from './transactions.columns';
import {
  filterTransactionEventOptions,
  formatDateForSearch,
  TransactionFilterOption,
} from './transactions.filters';

interface TransactionDateRange {
  start: Date | null;
  end: Date | null;
}

@Component({
  selector: 'app-transactions',
  templateUrl: './transactions.component.html',
  styleUrls: ['./transactions.component.css'],
  imports: [
    DateRangeComponent,
    InputComponent,
    ButtonComponent,
    SelectComponent,
    NgIcon,
    GsTableComponent,
    PaginationComponent,
  ],
  viewProviders: [provideIcons({ lucideSearch })],
})
export class TransactionsComponent implements OnInit, OnDestroy {
  private transactionService = inject(TransactionService);
  private activatedRoute = inject(ActivatedRoute);
  private router = inject(Router);
  private confService = inject(ConfigurationService);
  private dialogService = inject(AppDialogService);
  private loader = inject(LoaderService);
  private datePipe = new DatePipe('en-US');

  public loading = signal(false);

  public data: Array<Transaction>;
  public totalItemsInDb: number;
  public itemsPerPage: number;
  private subscriptions: Array<Subscription>;
  public transactionsStatus: TransactionStatusObj;
  public search: SearchQueryParams;
  public currentPage: number;
  public columns: ColumnDef<Transaction>[] = [];
  @ViewChild('pagination') pagination: PaginationComponent;
  public eventOptions: TransactionFilterOption[] = [];
  public statusOptions: TransactionFilterOption[] = [];
  public eventFilterControl = new FormControl('', { nonNullable: true });
  public eventSearchControl = new FormControl('', { nonNullable: true });
  public statusFilterControl = new FormControl('', { nonNullable: true });
  public accountFilterControl = new FormControl('', { nonNullable: true });
  public dateRange: TransactionDateRange = { start: null, end: null };

  public get filteredEventOptions(): TransactionFilterOption[] {
    return filterTransactionEventOptions(
      this.eventOptions,
      this.eventSearchControl.value
    );
  }

  constructor() {
    this.subscriptions = [];
    this.initializeSearch();
  }

  ngOnInit(): void {
    let allevents = this.activatedRoute.snapshot.data['events'];
    this.eventOptions = allevents.data.map(event => ({
      value: event.code,
      label: `${event.description} / ${event.code}`,
    }));

    this.transactionsStatus = {
      items: {
        PH: { id: 'PH', name: 'Pending Hold' },
        CA: { id: 'CA', name: 'Cancelled' },
        OK: { id: 'OK', name: 'Confirm' },
      },
      list: [
        { id: 'PH', name: 'Pending Hold' },
        { id: 'CA', name: 'Cancelled' },
        { id: 'OK', name: 'Confirm' },
      ],
    };
    this.statusOptions = [
      { label: 'All', value: '' },
      ...this.transactionsStatus.list.map(item => ({
        label: item.name,
        value: item.id,
      })),
    ];
    this.columns = buildTransactionColumns(
      this.transactionsStatus,
      this.datePipe,
      transaction => this.openModalEdit(transaction)
    );

    let resolved = this.activatedRoute.snapshot.data['api'];
    this.data = resolved.data;
    this.totalItemsInDb = resolved.metadata.total;
    this.itemsPerPage = resolved.metadata.perPage;

    const queryparams$ = this.activatedRoute.queryParams.subscribe(params => {
      this.currentPage = params['page'];
      let resolved = this.activatedRoute.snapshot.data['api'];

      if (!params['page'] || params['page'] > resolved.metadata.lastPage) {
        this.router.navigate(['.'], {
          relativeTo: this.activatedRoute,
          queryParams: { page: 1 },
        });
      }

      if (params['page']) {
        this.currentPage = params['page'];
        this.data = resolved.data;
        this.totalItemsInDb = resolved.metadata.total;
        this.itemsPerPage = resolved.metadata.perPage;
        if (this.pagination) {
          this.pagination.refresh(
            this.currentPage,
            this.totalItemsInDb,
            this.itemsPerPage
          );
        }
      }
    });

    const transactionsEventsSub =
      this.transactionService.TransactionEvents$.subscribe(event => {
        if (event.id === 'ERROR_LOG_OPENED') {
          this.data = this.data.map(item => {
            const isTargetTransaction = item.id === event.transactionId;

            return isTargetTransaction
              ? { ...item, hasUnreadErrors: false }
              : item;
          });
        }
      });

    this.subscriptions.push(queryparams$, transactionsEventsSub);
  }

  ngOnDestroy() {
    this.subscriptions.forEach(o => o.unsubscribe());
  }

  public refresh() {
    const all$ = this.transactionService
      .getAll$(this.currentPage)
      .subscribe(res => {
        this.data = res.data;
        this.totalItemsInDb = res.metadata.total;
        this.itemsPerPage = res.metadata.perPage;
        if (this.pagination) {
          this.pagination.refresh(
            this.currentPage,
            this.totalItemsInDb,
            this.itemsPerPage
          );
        }
        if (this.currentPage > res.metadata.lastPage) {
          this.currentPage = res.metadata.lastPage;
        }
        this.router.navigate(['/private/transactions'], {
          queryParams: { page: this.currentPage },
        });
      });
    this.subscriptions.push(all$);
  }

  private initializeSearch() {
    this.search = {
      params: {
        status: '',
        event: '',
        accountId: '',
        dateFrom: '',
        dateTo: '',
      },
      applied: false,
    };
    this.transactionService.search = JSON.parse(JSON.stringify(this.search));
    this.eventFilterControl.setValue('');
    this.eventSearchControl.setValue('');
    this.statusFilterControl.setValue('');
    this.accountFilterControl.setValue('');
    this.dateRange = { start: null, end: null };
  }

  public openModalEdit(item: Transaction) {
    this.dialogService.open(ViewComponent, {
      size: 'lg',
      inputs: { item, transactionStatus: this.transactionsStatus },
    });
  }

  public submit() {
    this.search.params.event = this.eventFilterControl.value;
    this.search.params.status = this.statusFilterControl.value;
    this.search.params.accountId = this.accountFilterControl.value;
    this.search.applied = true;
    this.transactionService.search = JSON.parse(JSON.stringify(this.search));
    if (this.currentPage != 1) {
      this.router.navigate(['/private/transactions'], {
        queryParams: { page: 1 },
      });
    } else {
      this.loading.set(true);
      const search$ = this.transactionService
        .getAll$(1, this.search)
        .pipe(finalize(() => this.loading.set(false)))
        .subscribe(res => {
          this.data = res.data;
          this.totalItemsInDb = res.metadata.total;
          this.itemsPerPage = res.metadata.perPage;
          this.currentPage = 1;
          if (this.pagination) {
            this.pagination.refresh(
              this.currentPage,
              this.totalItemsInDb,
              this.itemsPerPage
            );
          }
        });
      this.subscriptions.push(search$);
    }
  }

  public reset() {
    this.initializeSearch();

    if (this.currentPage != 1) {
      this.router.navigate(['/private/transactions'], {
        queryParams: { page: 1 },
      });
    } else {
      this.loading.set(true);
      const search$ = this.transactionService
        .getAll$(1)
        .pipe(finalize(() => this.loading.set(false)))
        .subscribe(res => {
          this.data = res.data;
          this.totalItemsInDb = res.metadata.total;
          this.itemsPerPage = res.metadata.perPage;
          this.currentPage = 1;
          if (this.pagination) {
            this.pagination.refresh(
              this.currentPage,
              this.totalItemsInDb,
              this.itemsPerPage
            );
          }
        });
      this.subscriptions.push(search$);
    }
  }

  public exportAsCsv() {
    this.transactionService.search = JSON.parse(JSON.stringify(this.search));
    this.loader.begin();
    this.transactionService
      .exportAsCsv(this.search)
      .pipe(
        take(1),
        finalize(() => this.loader.end())
      )
      .subscribe(data => {
        this.transactionService.downloadCsv(
          data,
          `transactions_${this.confService.clientName}`
        );
      });
  }

  public onDateRangeChange(range: TransactionDateRange) {
    this.dateRange = range;
    this.search.params.dateFrom = formatDateForSearch(range.start);
    this.search.params.dateTo = formatDateForSearch(range.end);
  }
}
